import {graphql} from 'gatsby'
import React from 'react'

import CardGrid from '@/sections/card-grid'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import {isValidTagColor} from '@/utils/is-valid-tag-color'

import type {
    CardGridFragment,
    CardGridSliceFragment,
    CustomerCaseCardGridFragment,
} from '@/graphql-types'
import type {Props as CardGridProps} from '@/sections/card-grid'
import type {LayoutProps} from '@/styles/grid'

type Props = {
    slice: CardGridFragment | CardGridSliceFragment | CustomerCaseCardGridFragment
    dataTestId?: string
}

const isAllowedLayout = (value: string): value is LayoutProps['$variant'] =>
    ['two-asymmetrical', 'two-equal', 'three-equal', 'four-equal'].includes(value)

const validateNumber = (number: number) => (isNaN(number) ? undefined : number)

/**
 *
 * This component has know issues with aliased fields while using Prismic Previews.
 * Due a limitation of "gatsby-plugin-prismic-previews", aliases do not work during Previews.
 * See more here https://github.com/prismicio/prismic-gatsby/blob/main/packages/gatsby-plugin-prismic-previews/docs/limitations.md
 */

export const CardGridSlice = React.forwardRef<HTMLDivElement, Props>(({slice, dataTestId}, ref) => {
    /**
     * initial_cards_shown was used to set the number cards shown on desktop.
     * and is now deprecated
     */
    const numInitialCardsShownOnMobile = slice.primary.initial_cards_shown_on_mobile ?? 4

    const initialCardsShownOnMobile = validateNumber(
        Number(numInitialCardsShownOnMobile),
    ) as CardGridProps['initialCardsShownOnMobile']

    const numInitialCardsShownOnDesktop =
        slice.primary.initial_cards_shown_on_desktop ?? slice.primary.initial_cards_shown

    const initialCardsShownOnDesktop = validateNumber(
        Number(numInitialCardsShownOnDesktop),
    ) as CardGridProps['initialCardsShownOnMobile']

    return (
        <CardGrid
            ref={ref}
            title={slice.primary.blockTitle?.richText}
            subtitle={slice.primary.subtitle}
            cards={slice.items.map((card) => {
                const tagBgColor = isValidTagColor(card.tag_color) ? card.tag_color : 'grey'

                const tag =
                    !!card.tag_text && !!tagBgColor
                        ? {
                              text: card.tag_text,
                              color: tagBgColor,
                          }
                        : undefined

                return {
                    ...card,
                    ctaText: slice.primary.card_cta_text,
                    filterKeywords: card.filterKeywords?.trim().split(/\s*,\s*/),
                    tag,
                }
            })}
            showMoreText={slice.primary.show_more_text}
            initialCardsShownOnDesktop={initialCardsShownOnDesktop}
            initialCardsShownOnMobile={initialCardsShownOnMobile}
            showFilter={slice.primary.show_filter}
            layout={isAllowedLayout(slice.primary.layout) ? slice.primary.layout : undefined}
            dataTestId={dataTestId}
            backgroundColor={
                isValidBackgroundColor(slice.primary.background_color)
                    ? slice.primary.background_color
                    : 'grey'
            }
            noPadding={slice.primary.no_padding}
        />
    )
})

export const cardGridSliceFragment = graphql`
    fragment CardGridSlice on PrismicSlicePageDataBodyCardGrid {
        __typename
        primary {
            blockTitle: title {
                richText
            }
            subtitle
            show_more_text
            initial_cards_shown
            initial_cards_shown_on_mobile
            initial_cards_shown_on_desktop
            card_cta_text
            show_filter
            layout
            background_color
            no_padding
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $fourColumns)
            }
            title
            body
            cta_link {
                ...PrismicLink
            }
            filterKeywords: filter_keywords
            tag_text
            tag_color
        }
    }
`
